/**
 * Live "N characters remaining" feedback for any field with a maxlength.
 *
 * Deliberately generic and string-free: the field says which node to write into
 * (data-character-counter="<id>") and what to write (data-counter-template, a dictionary value
 * resolved server-side). Hard-coding the copy here would put the one untranslatable string in the
 * middle of an otherwise fully dictionary-driven feature.
 *
 * This is feedback, not enforcement. `maxlength` is what stops the typing (or, for the rich-text bio,
 * the length guard in rich-text-field.js), and the server re-checks
 * the length against configuration regardless — a counter that lies, or a bundle that fails to
 * load, cannot let an over-long value through.
 */

/**
 * Fraction of the limit at which the counter turns amber. Not a setting: it is a visual detail of
 * this component, and nobody outside the dev team has an opinion about 0.1 versus 0.12.
 */
const WARNING_THRESHOLD = 0.1;

/**
 * How long the member's writing is.
 *
 * A field carrying `data-count-text-only` holds HTML — the rich-text bio syncs markup into its
 * textarea — and the member must be charged for words, not tags. Parsing and reading textContent is
 * the same measure the server takes with ProfileBioSanitiser.ToPlainText; counting the raw value
 * would bill somebody 17 characters for one bold word.
 */
function measure(field) {
  if (field.dataset.countTextOnly === undefined) return field.value.length;

  try {
    return new DOMParser().parseFromString(field.value, "text/html").body.textContent.length;
  } catch {
    return field.value.length;
  }
}

function updateCounter(field, counter, template, maxLength) {
  // String.length, matching the server's string.Length exactly — both count UTF-16 code units, so
  // an emoji costs two in both places and the number shown can never contradict the number
  // enforced.
  const remaining = maxLength - measure(field);

  counter.textContent = template.replace("{0}", remaining);
  counter.classList.toggle(
    "my-profile__character-counter--warning",
    remaining <= maxLength * WARNING_THRESHOLD
  );
}

function CharacterCounter() {
  const fields = document.querySelectorAll("[data-character-counter]");

  if (!fields?.length) return;

  fields.forEach((field) => {
    const counter = document.getElementById(field.dataset.characterCounter);

    // maxlength where the browser can enforce it; data-max-length where it cannot — a field holding
    // HTML has no maxlength, because it would count tags and truncate mid-tag.
    const maxLength = parseInt(field.getAttribute("maxlength") ?? field.dataset.maxLength, 10);
    const template = field.dataset.counterTemplate;

    // No counter node, no limit, or no copy to put in it: leave the field exactly as the server
    // rendered it rather than replacing correct markup with "undefined".
    if (!counter || !template || !Number.isFinite(maxLength) || maxLength <= 0) return;

    field.addEventListener("input", () =>
      updateCounter(field, counter, template, maxLength)
    );

    // Once on load, so a field the browser restored on back-navigation shows the right number
    // without waiting for a keystroke.
    updateCounter(field, counter, template, maxLength);
  });
}

function initCharacterCounter() {
  if (document.readyState !== "loading") {
    CharacterCounter();
  } else {
    document.addEventListener("DOMContentLoaded", CharacterCounter);
  }
}

export default initCharacterCounter;
